import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import {
  useOrderChangeRequests,
  useCreateOrderChangeRequest,
  useApproveOrderChangeRequest,
  useRejectOrderChangeRequest,
  useWithdrawOrderChangeRequest,
  useOrderWpwScope,
} from "@/features/orders/hooks/useOrderChanges";
import { useAvailableLineItemTypes } from "@/features/orders/hooks/useOrders";
import { useContractorPreview } from "../contractorPreview";
import { useOrderAccess } from "../orderAccess";
import {
  ORDER_CHANGE_REQUEST_STATUS_LABELS,
  ORDER_CHANGE_REQUEST_TYPE_LABELS,
  type AvailableLineItemType,
  type LineItemChange,
  type OrderChangeRequestType,
  type OrderLineItem,
  type WpwOwnerChangeRow,
} from "@/features/orders/types";
import type { LineItemOverride } from "@/features/orders/api/orderChanges";
import {
  WpwListChangeEditor,
  WpwListChangeSummary,
  wpwCountDelta,
  wpwPlanFromRows,
  wpwPlanIssue,
  wpwPlanToInput,
  type WpwPlanDraft,
} from "./WpwListChangeEditor";

interface Props {
  orderId: string;
  lineItems?: OrderLineItem[];
  orderStatus?: string;
  // Zamówienie WPW: zmiana liczby WPW niesie wybór właścicieli (lista WPW do pozyskania).
  orderType?: string;
}

const STATUS_COLORS: Record<string, "gray" | "blue" | "green" | "red" | "yellow"> = {
  pending: "yellow",
  approved: "green",
  rejected: "red",
  withdrawn: "gray",
};

interface LineItemDraft {
  order_line_item_id: string;
  line_item_type_name: string;
  line_item_type_unit: string;
  current_quantity: string;
  current_unit_rate: string;
  new_quantity: string;
  new_unit_rate: string;
  enabled: boolean;
}

interface NewItemDraft {
  _key: number;
  line_item_type_id: string;
  line_item_type_name: string;
  line_item_type_unit: string;
  new_quantity: string;
  new_unit_rate: string;
}

function fmt(val: string | null | undefined) {
  if (val == null) return "";
  const n = parseFloat(val);
  return isNaN(n) ? "" : n.toLocaleString("pl-PL", { minimumFractionDigits: 2, useGrouping: "always" });
}

let _draftKey = 0;
function nextKey() { return ++_draftKey; }

/** Message from a `{ data, errors, meta }` error response, or `fallback`. */
function apiErrorMessage(err: unknown, fallback: string): string {
  const res = (err as { response?: { status?: number; data?: { errors?: unknown } } })?.response;
  const errors = res?.data?.errors;
  if (typeof errors === "string" && errors) return errors;
  if (Array.isArray(errors) && errors.length) return errors.map(String).join(" ");
  if (errors && typeof errors === "object") {
    const obj = errors as Record<string, unknown>;
    if (typeof obj.detail === "string") return obj.detail;
    const flat = Object.values(obj).flat().filter(Boolean);
    if (flat.length) return flat.map(String).join(" ");
    return JSON.stringify(errors);
  }
  if (res?.status === 403) return "Brak uprawnień do tej operacji.";
  if (res?.status === 404) return "Nie znaleziono wniosku — mógł zostać wycofany lub nie masz do niego dostępu.";
  if (!res) return `${fallback} (brak odpowiedzi serwera).`;
  return `${fallback} (HTTP ${res.status ?? "?"}).`;
}

export function OrderChangePanel({ orderId, lineItems = [], orderStatus, orderType }: Props) {
  const user = useAuthStore((s) => s.user);
  // W „widoku podwykonawcy" (podgląd GK) panel renderuje wariant podwykonawcy,
  // a składanie wniosków jest wyłączone.
  const { isPreview } = useContractorPreview();
  // Dostęp z nadanego grantu (karta GK): „Podgląd" = bez formularza i decyzji,
  // „Zarządzanie" = składanie i rozpatrywanie jak koordynator (backend:
  // CanManageOrderObject na approve/reject, bramka zapisu na create).
  const { readOnly: accessReadOnly, canManage: accessCanManage } = useOrderAccess();
  const isContractor = user?.role === "contractor" || isPreview;
  const isDirector = !isPreview && user?.role === "director";
  const isGK = isDirector || (!isPreview && (user?.role === "coordinator" || accessCanManage));
  // Rozpatrywanie wniosku bramkuje backend przez IsTechnicalDirectorOrCoordinator:
  // prezes, dyrektor TECHNICZNY albo koordynator. Dyrektor nietechniczny (kadrowy)
  // widział „Zatwierdź"/„Odrzuć" i dostawał 403 z API.
  const canReviewChanges =
    !isPreview &&
    !accessReadOnly &&
    (!!user?.is_ceo ||
      (user?.role === "director" && !!user?.is_technical_director) ||
      user?.role === "coordinator" ||
      accessCanManage);
  // „Zmiana GK (z prośbą o akceptację)" jest adresowana do podwykonawcy — to on ją
  // zatwierdza albo odrzuca (backend: _contractor_decision_block). Własne wnioski
  // podwykonawcy i zmiany „tylko powiadomienie" rozpatruje wyłącznie GK.
  // W „Widoku podwykonawcy" te same przyciski co u podwykonawcy, tylko wyłączone.
  const canReview = (req: { change_type: OrderChangeRequestType }) =>
    canReviewChanges ||
    ((isPreview || user?.role === "contractor") && req.change_type === "gk_with_approval");
  // „Wycofaj" ma autor wniosku (i dyrektor). Podgląd nie zna konta podwykonawcy — jego
  // wnioski to te typu CONTRACTOR (innych podwykonawca składać nie może).
  const canWithdraw = (req: { requested_by: string; change_type: OrderChangeRequestType }) =>
    isPreview
      ? req.change_type === "contractor"
      : req.requested_by === user?.id || isDirector;

  const { data: requests = [], isLoading } = useOrderChangeRequests({ order: orderId });
  const createMutation = useCreateOrderChangeRequest(orderId);
  const approveMutation = useApproveOrderChangeRequest();
  const rejectMutation = useRejectOrderChangeRequest();
  const withdrawMutation = useWithdrawOrderChangeRequest();

  // Wejście z sekwencji masowej WPW (krok 5 → „Zakończ bez zamówienia" → wybór
  // zamówienia): `?wniosek=zmiana` otwiera formularz od razu i przewija do panelu, a
  // location.state `wpwPrefillOwners` (właściciele ze skompilowanym pakietem) wstępnie
  // zaznacza „Listę WPW do pozyskania". Bez state formularz otwiera się pusty.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const openFromUrl = searchParams.get("wniosek") === "zmiana";
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (openFromUrl && typeof panelRef.current?.scrollIntoView === "function") {
      panelRef.current.scrollIntoView({ block: "start" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const prefillOwners = useMemo<string[]>(() => {
    const st = location.state as { wpwPrefillOwners?: unknown } | null;
    return Array.isArray(st?.wpwPrefillOwners)
      ? st.wpwPrefillOwners.filter((x): x is string => typeof x === "string")
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch available line item types when form is open (only for GK/director)
  const [showForm, setShowForm] = useState(openFromUrl);
  const { data: availableTypes = [], isLoading: typesLoading } = useAvailableLineItemTypes(orderId, showForm && (isGK || isContractor));

  const defaultChangeType: OrderChangeRequestType = isGK ? "gk_with_approval" : "contractor";
  const isWpwOrder = orderType === "wpw_terenowe" || orderType === "wpw_kompleksowe";

  // ── Creation form state ────────────────────────────────────────────────────
  const [form, setForm] = useState({
    description: "",
    new_deadline: "",
    new_description: "",
    change_type: defaultChangeType,
  });
  const [formErr, setFormErr] = useState<string | null>(null);
  const buildDrafts = (items: OrderLineItem[]): LineItemDraft[] =>
    items.map((li) => ({
      order_line_item_id: li.id,
      line_item_type_name: li.line_item_type_name,
      line_item_type_unit: li.line_item_type_unit,
      current_quantity: li.quantity,
      current_unit_rate: li.unit_rate,
      new_quantity: li.quantity,
      new_unit_rate: li.unit_rate,
      enabled: false,
    }));

  const [lineItemDrafts, setLineItemDrafts] = useState<LineItemDraft[]>(() => buildDrafts(lineItems));

  // Re-sync when lineItems prop arrives (order data may load after mount)
  useEffect(() => {
    setLineItemDrafts(buildDrafts(lineItems));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems.length]);
  const [newItemDrafts, setNewItemDrafts] = useState<NewItemDraft[]>([]);
  // Lista WPW do pozyskania zmieniana razem z liczbą WPW — tylko GK, tylko zamówienia WPW.
  // Nie trafia do treści wniosku; backend pamięta ją i stosuje przy akceptacji.
  const [wpwPlan, setWpwPlan] = useState<WpwPlanDraft>({});

  const resetForm = () => {
    setForm({ description: "", new_deadline: "", new_description: "", change_type: defaultChangeType });
    setLineItemDrafts(buildDrafts(lineItems));
    setNewItemDrafts([]);
    setWpwPlan({});
    setFormErr(null);
  };

  // Zamknięcie formularza (Anuluj / po wysłaniu) zdejmuje też `?wniosek=zmiana` z URL,
  // żeby F5 nie otwierał go ponownie.
  const closeForm = () => {
    resetForm();
    setShowForm(false);
    if (openFromUrl) {
      setSearchParams((p) => { p.delete("wniosek"); return p; }, { replace: true });
    }
  };

  const updateDraft = (id: string, field: keyof LineItemDraft, value: string | boolean) =>
    setLineItemDrafts((prev) =>
      prev.map((d) => (d.order_line_item_id === id ? { ...d, [field]: value } : d))
    );

  const addNewItem = () => {
    const first = availableTypes[0];
    if (!first) return;
    setNewItemDrafts((prev) => [
      ...prev,
      {
        _key: nextKey(),
        line_item_type_id: first.id,
        line_item_type_name: first.name,
        line_item_type_unit: first.unit,
        new_quantity: "1",
        new_unit_rate: first.base_rate,
      },
    ]);
  };

  const updateNewItem = (key: number, field: keyof Omit<NewItemDraft, "_key">, value: string) => {
    setNewItemDrafts((prev) =>
      prev.map((d) => {
        if (d._key !== key) return d;
        if (field === "line_item_type_id") {
          const type = availableTypes.find((t) => t.id === value);
          if (type) {
            return { ...d, line_item_type_id: type.id, line_item_type_name: type.name, line_item_type_unit: type.unit, new_unit_rate: type.base_rate };
          }
        }
        return { ...d, [field]: value };
      })
    );
  };

  const removeNewItem = (key: number) =>
    setNewItemDrafts((prev) => prev.filter((d) => d._key !== key));

  const handleCreate = async () => {
    if (!form.description.trim()) {
      setFormErr("Opis zmian jest wymagany.");
      return;
    }
    const changedItems: LineItemChange[] = lineItemDrafts
      .filter((d) => d.enabled)
      .map((d) => ({
        order_line_item_id: d.order_line_item_id,
        line_item_type_name: d.line_item_type_name,
        line_item_type_unit: d.line_item_type_unit,
        current_quantity: d.current_quantity,
        current_unit_rate: d.current_unit_rate,
        new_quantity: d.new_quantity || d.current_quantity,
        new_unit_rate: d.new_unit_rate || d.current_unit_rate,
      }));

    const newItems: LineItemChange[] = newItemDrafts.map((d) => ({
      order_line_item_id: null,
      line_item_type_id: d.line_item_type_id,
      line_item_type_name: d.line_item_type_name,
      line_item_type_unit: d.line_item_type_unit,
      current_quantity: "0",
      current_unit_rate: "0",
      new_quantity: d.new_quantity,
      new_unit_rate: d.new_unit_rate,
    }));

    const sendWpwPlan = wpwEditsOnCreate && Object.keys(wpwPlan).length > 0;
    if (wpwEditsOnCreate) {
      const issue = wpwPlanIssue(wpwScope, createWpwDelta, wpwPlan);
      if (issue) {
        setFormErr(issue);
        return;
      }
    }

    try {
      await createMutation.mutateAsync({
        order: orderId,
        change_type: isContractor ? "contractor" : form.change_type,
        description: form.description,
        new_deadline: form.new_deadline || null,
        new_description: form.new_description || undefined,
        line_item_changes: [...changedItems, ...newItems],
        ...(sendWpwPlan ? { wpw_owner_changes: wpwPlanToInput(wpwPlan) } : {}),
      });
      closeForm();
    } catch (err: unknown) {
      setFormErr(apiErrorMessage(err, "Błąd zapisu wniosku"));
    }
  };

  // ── Approval modal state ───────────────────────────────────────────────────
  const [reviewModal, setReviewModal] = useState<{
    id: string;
    action: "approve" | "reject";
    proposed: LineItemChange[];
  } | null>(null);
  const [reviewComment, setReviewComment] = useState("");
  // Error of the last approve/reject attempt — shown inside the modal. A silent
  // failure here left the contractor clicking „Zatwierdź" with nothing happening.
  const [reviewErr, setReviewErr] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, LineItemOverride>>({});
  // For new items in approval modal — keyed by line_item_type_id + index
  const [newItemOverrides, setNewItemOverrides] = useState<Record<string, { new_quantity: string; new_unit_rate: string }>>({});

  // GK przy akceptacji może uzupełnić/poprawić listę WPW (wniosek podwykonawcy przychodzi bez niej).
  const [reviewWpwPlan, setReviewWpwPlan] = useState<WpwPlanDraft>({});
  const wpwEditsOnCreate = showForm && isGK && isWpwOrder;
  const wpwEditsOnReview = !!reviewModal && reviewModal.action === "approve" && canReviewChanges && isWpwOrder;
  const { data: wpwScope, isLoading: wpwScopeLoading } = useOrderWpwScope(
    orderId,
    wpwEditsOnCreate || wpwEditsOnReview,
  );
  const createWpwDelta = wpwCountDelta(
    wpwScope,
    lineItemDrafts.filter((d) => d.enabled).map((d) => ({ order_line_item_id: d.order_line_item_id, new_quantity: d.new_quantity })),
  );
  // Lista WPW jest aktywna dopiero, gdy pozycja liczby WPW jest ZAZNACZONA w tabeli
  // pozycji — także przy ilości bez zmian (zamiana pakietu, inny właściciel w miejsce
  // dotychczasowego). Odznaczenie pozycji czyści wybory listy, żeby nic nie poszło po cichu.
  const countLine = wpwScope?.count_line_item ?? null;
  const countLineEnabled =
    !countLine || lineItemDrafts.some((d) => d.order_line_item_id === countLine.id && d.enabled);
  useEffect(() => {
    if (!countLineEnabled) setWpwPlan((prev) => (Object.keys(prev).length ? {} : prev));
  }, [countLineEnabled]);
  const wpwDisabledReason = countLine && !countLineEnabled
    ? `Zaznacz pozycję „${countLine.name}” w tabeli pozycji (także bez zmiany ilości), żeby zmieniać listę WPW.`
    : undefined;
  // Właściciele z pakietem z sekwencji masowej WPW są tylko PODŚWIETLANI w edytorze —
  // bez zaznaczania i bez ruszania ilości. Użytkownik sam decyduje: zamiana pakietu
  // (usuń jeden WPW, dodaj podświetlonego; ilość bez zmian), nowy WPW (zwiększ ilość)
  // albo dwa pakiety za jeden (zmniejsz ilość).
  const highlightOwners = useMemo(() => new Set(prefillOwners), [prefillOwners]);
  const highlightedInScope = useMemo(
    () => (wpwScope ? [...wpwScope.candidates, ...wpwScope.handovers].filter((c) => highlightOwners.has(c.owner_id)).length : 0),
    [wpwScope, highlightOwners],
  );
  const prefillNote = !prefillOwners.length || !wpwScope
    ? null
    : highlightedInScope
      ? `Z sekwencji masowej WPW: ${highlightedInScope} właścicieli z pakietem podświetlonych na liście poniżej — nikogo nie zaznaczono, ilości nie zmieniono.`
      : "Żaden właściciel z sekwencji masowej nie występuje na liście tego zamówienia (zgoda pozyskana albo działka poza zakresem).";

  const openReviewModal = (
    id: string,
    action: "approve" | "reject",
    proposed: LineItemChange[],
    wpwRows?: WpwOwnerChangeRow[],
  ) => {
    setReviewModal({ id, action, proposed });
    setReviewWpwPlan(wpwPlanFromRows(wpwRows));
    setReviewComment("");
    setReviewErr(null);
    const init: Record<string, LineItemOverride> = {};
    proposed.filter((p) => p.order_line_item_id).forEach((p) => {
      init[p.order_line_item_id as string] = {
        order_line_item_id: p.order_line_item_id as string,
        new_quantity: p.new_quantity,
        new_unit_rate: p.new_unit_rate,
      };
    });
    setOverrides(init);
    // Pre-populate new item overrides
    const newInit: Record<string, { new_quantity: string; new_unit_rate: string }> = {};
    proposed.filter((p) => !p.order_line_item_id).forEach((p, i) => {
      const key = `new_${i}`;
      newInit[key] = { new_quantity: p.new_quantity, new_unit_rate: p.new_unit_rate };
    });
    setNewItemOverrides(newInit);
  };

  // Only GK may send `line_item_overrides` (the director edits them in the modal).
  // The contractor approves a GK request AS PROPOSED — sending the prefilled table
  // back made the backend answer 400 („Podwykonawca zatwierdza wniosek w
  // zaproponowanej postaci…"), which the modal used to swallow.
  const buildOverrides = (proposed: LineItemChange[]): LineItemOverride[] | undefined => {
    if (!canReviewChanges) return undefined;
    const existingOverrides = Object.values(overrides);
    // Build new item overrides in same order as proposed new items
    const newItems = proposed.filter((p) => !p.order_line_item_id);
    const newItemFinal = newItems.map((p, i) => {
      const ov = newItemOverrides[`new_${i}`] ?? { new_quantity: p.new_quantity, new_unit_rate: p.new_unit_rate };
      return {
        order_line_item_id: null as string | null,
        line_item_type_id: p.line_item_type_id,
        new_quantity: ov.new_quantity,
        new_unit_rate: ov.new_unit_rate,
      };
    });
    const finalOverrides = [...existingOverrides, ...newItemFinal];
    return finalOverrides.length > 0 ? finalOverrides : undefined;
  };

  // Ilość pozycji WPW po ewentualnych poprawkach dyrektora w oknie akceptacji.
  const reviewWpwDelta = reviewModal
    ? wpwCountDelta(
        wpwScope,
        reviewModal.proposed
          .filter((p) => p.order_line_item_id)
          .map((p) => ({
            order_line_item_id: p.order_line_item_id,
            new_quantity: overrides[p.order_line_item_id as string]?.new_quantity ?? p.new_quantity,
          })),
      )
    : null;

  const handleReview = async () => {
    if (!reviewModal) return;
    setReviewErr(null);
    if (reviewModal.action === "approve" && wpwEditsOnReview) {
      const issue = wpwPlanIssue(wpwScope, reviewWpwDelta, reviewWpwPlan);
      if (issue) {
        setReviewErr(issue);
        return;
      }
    }
    try {
      if (reviewModal.action === "approve") {
        await approveMutation.mutateAsync({
          id: reviewModal.id,
          comment: reviewComment,
          line_item_overrides: buildOverrides(reviewModal.proposed),
          ...(wpwEditsOnReview && wpwScope ? { wpw_owner_changes: wpwPlanToInput(reviewWpwPlan) } : {}),
        });
      } else {
        await rejectMutation.mutateAsync({ id: reviewModal.id, comment: reviewComment });
      }
    } catch (err: unknown) {
      // Keep the modal open with the reason — every 4xx/5xx must reach the user.
      setReviewErr(
        apiErrorMessage(
          err,
          reviewModal.action === "approve"
            ? "Nie udało się zatwierdzić wniosku"
            : "Nie udało się odrzucić wniosku",
        ),
      );
      return;
    }
    setReviewModal(null);
    setReviewComment("");
    setReviewErr(null);
    setOverrides({});
    setNewItemOverrides({});
  };

  const canSubmit = (isContractor || isGK) && orderStatus !== "rejected";
  // `?wniosek=zmiana` może otworzyć formularz komuś bez prawa składania — bramka zostaje.
  const formOpen = showForm && canSubmit;

  // Withdrawn (cancelled) requests are collapsed by default and pushed to the
  // end of the list so active/decided requests stay in focus.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const sortedRequests = [...requests].sort(
    (a, b) => Number(a.status === "withdrawn") - Number(b.status === "withdrawn")
  );

  // Current order value after all applied (approved) changes — computed live from the order's
  // line items, which already reflect every approved change request. Shown only to GK, since the
  // panel gates rate/value visibility on the same role (contractors get total_value=null).
  const ratesVisible = lineItems.length > 0 && lineItems.every((li) => li.total_value != null);
  const currentOrderValue = lineItems.reduce(
    (sum, li) => sum + (li.total_value != null ? parseFloat(li.total_value) : 0),
    0
  );

  return (
    <div ref={panelRef}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800 dark:text-gray-200">Wnioski o zmianę zamówienia</h3>
        {canSubmit && (
          <Button size="sm" disabled={isPreview} onClick={() => { if (showForm) { closeForm(); } else { setShowForm(true); } }}>
            {showForm ? "Anuluj" : "+ Nowy wniosek"}
          </Button>
        )}
      </div>

      {/* Aktualna wartość zamówienia po zmianach — odzwierciedla wszystkie zatwierdzone wnioski
          (ilość i stawki), tak by efekt zmiany był widoczny od razu w tej sekcji. */}
      {isGK && ratesVisible && (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-2.5 mb-4">
          <span className="text-xs font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Aktualna wartość zamówienia (po zmianach)
          </span>
          <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {currentOrderValue.toLocaleString("pl-PL", { minimumFractionDigits: 2, useGrouping: "always" })} PLN
          </span>
        </div>
      )}

      {/* ── Creation form ────────────────────────────────────────────────── */}
      {formOpen && (
        <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4 flex flex-col gap-3">
          {isGK && (
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Typ zmiany</label>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
                value={form.change_type}
                onChange={(e) => setForm({ ...form, change_type: e.target.value as OrderChangeRequestType })}
              >
                <option value="gk_with_approval">Z prośbą o akceptację przez Podwykonawcę</option>
                {isDirector && (
                  <option value="gk_notification_only">Tylko powiadomienie (tryb Dyrektora)</option>
                )}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Opis proponowanych zmian <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Opisz co ma ulec zmianie i dlaczego"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Nowy termin (opcja)</label>
              <DatePicker
                value={form.new_deadline}
                onChange={(v) => setForm({ ...form, new_deadline: v })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Nowy opis zamówienia (opcja)</label>
              <input
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
                value={form.new_description}
                onChange={(e) => setForm({ ...form, new_description: e.target.value })}
                placeholder="Zmieniony opis"
              />
            </div>
          </div>

          {/* Existing line items — modify */}
          {lineItemDrafts.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Zmiany istniejących pozycji <span className="text-gray-400 font-normal">(zaznacz pozycje do zmiany)</span>
              </label>

              {/* Mobile card layout */}
              <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 rounded border border-gray-200 dark:border-gray-700">
                {lineItemDrafts.map((d) => (
                  <div
                    key={d.order_line_item_id}
                    className={`p-3 ${d.enabled ? "bg-yellow-50 dark:bg-yellow-900/10" : ""}`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0"
                        checked={d.enabled}
                        onChange={(e) => updateDraft(d.order_line_item_id, "enabled", e.target.checked)}
                      />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {d.line_item_type_name}
                        <span className="text-gray-400 ml-1 font-normal">[{d.line_item_type_unit}]</span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 pl-5">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Ilość</span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-400">{fmt(d.current_quantity)}</span>
                        <span className="text-gray-300 dark:text-gray-600 text-xs">→</span>
                        <input
                          type="number"
                          disabled={!d.enabled}
                          value={d.new_quantity}
                          onChange={(e) => updateDraft(d.order_line_item_id, "new_quantity", e.target.value)}
                          className="w-20 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-right text-xs bg-white dark:bg-gray-700 disabled:opacity-40"
                        />
                      </div>
                    </div>
                    {isGK && (
                      <div className="flex items-center justify-between gap-2 pl-5 mt-1.5">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Stawka</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">{fmt(d.current_unit_rate)} zł</span>
                          <span className="text-gray-300 dark:text-gray-600 text-xs">→</span>
                          <input
                            type="number"
                            disabled={!d.enabled}
                            value={d.new_unit_rate}
                            onChange={(e) => updateDraft(d.order_line_item_id, "new_unit_rate", e.target.value)}
                            className="w-20 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-right text-xs bg-white dark:bg-gray-700 disabled:opacity-40"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop table layout */}
              <div className="hidden sm:block rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    <tr>
                      <th className="px-2 py-1.5 text-left w-6 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"></th>
                      <th className="px-2 py-1.5 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                      <th className="px-2 py-1.5 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość (obecna → nowa)</th>
                      {isGK && <th className="px-2 py-1.5 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka (obecna → nowa)</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {lineItemDrafts.map((d) => (
                      <tr
                        key={d.order_line_item_id}
                        className={`border-t border-gray-100 dark:border-gray-700 ${d.enabled ? "bg-yellow-50 dark:bg-yellow-900/10" : ""}`}
                      >
                        <td className="px-2 py-1.5 text-center">
                          <input
                            type="checkbox"
                            checked={d.enabled}
                            onChange={(e) => updateDraft(d.order_line_item_id, "enabled", e.target.checked)}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">
                          {d.line_item_type_name}
                          <span className="text-gray-400 ml-1">[{d.line_item_type_unit}]</span>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <span className="text-gray-400">{fmt(d.current_quantity)}</span>
                          <span className="mx-1 text-gray-300">→</span>
                          <input
                            type="number"
                            disabled={!d.enabled}
                            value={d.new_quantity}
                            onChange={(e) => updateDraft(d.order_line_item_id, "new_quantity", e.target.value)}
                            className="w-20 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-right text-xs bg-white dark:bg-gray-700 disabled:opacity-40"
                          />
                        </td>
                        {isGK && (
                          <td className="px-2 py-1.5 text-right">
                            <span className="text-gray-400">{fmt(d.current_unit_rate)} zł</span>
                            <span className="mx-1 text-gray-300">→</span>
                            <input
                              type="number"
                              disabled={!d.enabled}
                              value={d.new_unit_rate}
                              onChange={(e) => updateDraft(d.order_line_item_id, "new_unit_rate", e.target.value)}
                              className="w-20 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-right text-xs bg-white dark:bg-gray-700 disabled:opacity-40"
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* New line items — add */}
          {(isGK || isContractor) && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Nowe pozycje kosztowe
                </label>
                <button
                  type="button"
                  onClick={addNewItem}
                  disabled={typesLoading || availableTypes.length === 0}
                  title={!typesLoading && availableTypes.length === 0 ? "Brak dostępnych typów pozycji w umowie ramowej" : undefined}
                  className="text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {typesLoading ? "Ładowanie…" : "+ Dodaj pozycję"}
                </button>
              </div>

              {newItemDrafts.length > 0 && (
                <div className="rounded border border-blue-200 dark:border-blue-800 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-blue-50 dark:bg-blue-900/20 text-gray-600 dark:text-gray-300">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Typ pozycji</th>
                        <th className="px-2 py-1.5 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość</th>
                        <th className="px-2 py-1.5 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka</th>
                        <th className="px-2 py-1.5 w-6 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {newItemDrafts.map((d) => (
                        <tr key={d._key} className="border-t border-blue-100 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10">
                          <td className="px-2 py-1.5">
                            <select
                              value={d.line_item_type_id}
                              onChange={(e) => updateNewItem(d._key, "line_item_type_id", e.target.value)}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-xs bg-white dark:bg-gray-700"
                            >
                              {availableTypes.map((t: AvailableLineItemType) => (
                                <option key={t.id} value={t.id}>
                                  {t.name} [{t.unit}] — {t.agreement_title}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <input
                              type="number"
                              value={d.new_quantity}
                              onChange={(e) => updateNewItem(d._key, "new_quantity", e.target.value)}
                              className="w-20 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-right text-xs bg-white dark:bg-gray-700"
                            />
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <input
                              type="number"
                              value={d.new_unit_rate}
                              disabled={isContractor}
                              onChange={(e) => updateNewItem(d._key, "new_unit_rate", e.target.value)}
                              className="w-20 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-right text-xs bg-white dark:bg-gray-700 disabled:opacity-60 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                            />
                            <span className="text-gray-400 ml-1">zł</span>
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <button
                              type="button"
                              onClick={() => removeNewItem(d._key)}
                              className="text-red-400 hover:text-red-600 text-base leading-none"
                              title="Usuń"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {availableTypes.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Brak dostępnych typów pozycji dla tego podwykonawcy.
                </p>
              )}
            </div>
          )}

          {wpwEditsOnCreate && prefillNote && (
            <p className="text-[11px] text-emerald-700 dark:text-emerald-400" data-testid="wpw-prefill-note">
              {prefillNote}
            </p>
          )}
          {wpwEditsOnCreate && (
            <WpwListChangeEditor
              scope={wpwScope}
              loading={wpwScopeLoading}
              delta={createWpwDelta}
              value={wpwPlan}
              onChange={setWpwPlan}
              disabled={!countLineEnabled}
              disabledReason={wpwDisabledReason}
              highlight={highlightOwners}
            />
          )}

          {formErr && <p className="text-xs text-red-600">{formErr}</p>}
          <div className="flex gap-2">
            <Button size="sm" loading={createMutation.isPending} onClick={handleCreate}>Wyślij wniosek</Button>
            <Button size="sm" variant="secondary" onClick={closeForm}>Anuluj</Button>
          </div>
        </div>
      )}

      {/* ── Change requests list ─────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : requests.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak wniosków o zmianę.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {sortedRequests.map((req) => {
            const isCollapsible = req.status === "withdrawn";
            const isExpanded = !isCollapsible || expandedIds.has(req.id);
            return (
            <div
              key={req.id}
              onClick={!isExpanded ? () => toggleExpanded(req.id) : undefined}
              className={`bg-white dark:bg-gray-800 border rounded-lg p-4 ${req.status === "pending" ? "border-yellow-300 dark:border-yellow-700" : "border-gray-200 dark:border-gray-700"}${!isExpanded ? " cursor-pointer opacity-75 hover:opacity-100" : ""}`}
            >
              <div className={`flex items-start justify-between ${isExpanded ? "mb-2" : ""}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge color={STATUS_COLORS[req.status] ?? "gray"}>
                    {ORDER_CHANGE_REQUEST_STATUS_LABELS[req.status]}
                  </Badge>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {ORDER_CHANGE_REQUEST_TYPE_LABELS[req.change_type]}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {req.requested_by_name} · {new Date(req.created_at).toLocaleDateString("pl")}
                  </span>
                </div>
                <div className="flex gap-2 ml-2">
                  {req.status === "pending" && canReview(req) && (
                    <>
                      <button
                        onClick={() => openReviewModal(req.id, "approve", req.line_item_changes ?? [], req.wpw_owner_changes)}
                        disabled={isPreview}
                        title={isPreview ? "Podgląd — akcje niedostępne" : undefined}
                        className="text-xs text-green-600 hover:underline font-medium disabled:opacity-50 disabled:no-underline"
                      >
                        Zatwierdź
                      </button>
                      <button
                        onClick={() => openReviewModal(req.id, "reject", [])}
                        disabled={isPreview}
                        title={isPreview ? "Podgląd — akcje niedostępne" : undefined}
                        className="text-xs text-red-600 hover:underline font-medium disabled:opacity-50 disabled:no-underline"
                      >
                        Odrzuć
                      </button>
                    </>
                  )}
                  {req.status === "pending" && canWithdraw(req) && (
                    <button
                      onClick={() => withdrawMutation.mutate(req.id)}
                      disabled={isPreview}
                      title={isPreview ? "Podgląd — akcje niedostępne" : undefined}
                      className="text-xs text-gray-500 dark:text-gray-400 hover:underline disabled:opacity-50 disabled:no-underline"
                    >
                      Wycofaj
                    </button>
                  )}
                  {isCollapsible && (
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleExpanded(req.id); }}
                      className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                    >
                      {isExpanded ? "Zwiń" : "Rozwiń szczegóły"}
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && (
              <>
              <p className="text-sm text-gray-800 dark:text-gray-200 mb-2">{req.description}</p>

              {(req.new_deadline || req.new_description) && (
                <div className="text-xs text-gray-500 dark:text-gray-400 flex gap-4 mb-2">
                  {req.new_deadline && <span>Nowy termin: <strong>{formatDate(req.new_deadline)}</strong></span>}
                  {req.new_description && <span>Nowy opis: <strong>{req.new_description}</strong></span>}
                </div>
              )}

              {/* Proposed line item changes summary */}
              {req.line_item_changes && req.line_item_changes.length > 0 && (
                <div className="mt-2 rounded border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-2 py-1 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                        <th className="px-2 py-1 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość</th>
                        <th className="px-2 py-1 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka</th>
                        <th className="px-2 py-1 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zmiana wartości</th>
                      </tr>
                    </thead>
                    <tbody>
                      {req.line_item_changes.map((ch, idx) => {
                        const isNew = !ch.order_line_item_id;
                        const oldVal = parseFloat(ch.current_quantity) * parseFloat(ch.current_unit_rate);
                        const newVal = parseFloat(ch.new_quantity) * parseFloat(ch.new_unit_rate);
                        const diff = newVal - oldVal;
                        return (
                          <tr
                            key={ch.order_line_item_id ?? `new_${idx}`}
                            className={`border-t border-gray-100 dark:border-gray-700 ${isNew ? "bg-blue-50/50 dark:bg-blue-900/10" : ""}`}
                          >
                            <td className="px-2 py-1 text-gray-700 dark:text-gray-300">
                              {isNew && (
                                <span className="inline-block text-[10px] font-medium bg-blue-100 dark:bg-blue-900/40 text-[var(--color-primary)] dark:text-blue-300 rounded px-1 mr-1">NOWA</span>
                              )}
                              {ch.line_item_type_name}
                              <span className="text-gray-400 ml-1">[{ch.line_item_type_unit}]</span>
                            </td>
                            <td className="px-2 py-1 text-right">
                              {isNew ? (
                                <span className="font-medium text-[var(--color-primary)] dark:text-blue-400">{fmt(ch.new_quantity)}</span>
                              ) : ch.current_quantity !== ch.new_quantity ? (
                                <span>
                                  <span className="line-through text-gray-400">{fmt(ch.current_quantity)}</span>
                                  {" → "}
                                  <span className="font-medium text-[var(--color-primary)] dark:text-blue-400">{fmt(ch.new_quantity)}</span>
                                </span>
                              ) : (
                                <span className="text-gray-400">{fmt(ch.current_quantity)}</span>
                              )}
                            </td>
                            <td className="px-2 py-1 text-right">
                              {isNew ? (
                                <span className="font-medium text-[var(--color-primary)] dark:text-blue-400">{fmt(ch.new_unit_rate)} zł</span>
                              ) : ch.current_unit_rate !== ch.new_unit_rate ? (
                                <span>
                                  <span className="line-through text-gray-400">{fmt(ch.current_unit_rate)} zł</span>
                                  {" → "}
                                  <span className="font-medium text-[var(--color-primary)] dark:text-blue-400">{fmt(ch.new_unit_rate)} zł</span>
                                </span>
                              ) : (
                                <span className="text-gray-400">{fmt(ch.current_unit_rate)} zł</span>
                              )}
                            </td>
                            <td className={`px-2 py-1 text-right font-medium ${diff > 0 ? "text-red-600" : diff < 0 ? "text-green-600" : "text-gray-400"}`}>
                              {!isNaN(diff) ? `${isNew ? "+" : diff > 0 ? "+" : ""}${diff.toLocaleString("pl-PL", { minimumFractionDigits: 2, useGrouping: "always" })} zł` : ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {!isPreview && <WpwListChangeSummary rows={req.wpw_owner_changes ?? []} />}

              {req.review_comment && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded px-2 py-1">
                  Komentarz: {req.review_comment} — {req.reviewed_by_name}
                </div>
              )}
              </>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* ── Review / approval modal ──────────────────────────────────────── */}
      {reviewModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-xl flex flex-col max-h-[90vh]">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {reviewModal.action === "approve" ? "Zatwierdź wniosek o zmianę" : "Odrzuć wniosek o zmianę"}
              </h3>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-4 flex flex-col gap-4">

              {/* Existing item changes */}
              {reviewModal.action === "approve" && reviewModal.proposed.filter((p) => p.order_line_item_id).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Zmiany istniejących pozycji
                    {isDirector && <span className="text-gray-400 font-normal ml-1">(dyrektor może zmienić wartości)</span>}
                  </p>
                  <div className="rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                          <th className="px-2 py-1.5 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość</th>
                          <th className="px-2 py-1.5 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reviewModal.proposed.filter((p) => p.order_line_item_id).map((p) => {
                          const ov = overrides[p.order_line_item_id as string] ?? { order_line_item_id: p.order_line_item_id as string, new_quantity: p.new_quantity, new_unit_rate: p.new_unit_rate };
                          return (
                            <tr key={p.order_line_item_id} className="border-t border-gray-100 dark:border-gray-700">
                              <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">
                                {p.line_item_type_name}
                                <span className="text-gray-400 ml-1">[{p.line_item_type_unit}]</span>
                                <div className="text-gray-400 text-[10px]">
                                  zaproponowano: {fmt(p.new_quantity)} × {fmt(p.new_unit_rate)} zł
                                </div>
                              </td>
                              <td className="px-2 py-1.5 text-right">
                                <input
                                  type="number"
                                  disabled={!isDirector}
                                  value={ov.new_quantity ?? ""}
                                  onChange={(e) =>
                                    setOverrides((prev) => ({
                                      ...prev,
                                      [p.order_line_item_id as string]: { ...ov, new_quantity: e.target.value },
                                    }))
                                  }
                                  className="w-20 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-right bg-white dark:bg-gray-700 disabled:opacity-60"
                                />
                              </td>
                              <td className="px-2 py-1.5 text-right">
                                <input
                                  type="number"
                                  disabled={!isDirector}
                                  value={ov.new_unit_rate ?? ""}
                                  onChange={(e) =>
                                    setOverrides((prev) => ({
                                      ...prev,
                                      [p.order_line_item_id as string]: { ...ov, new_unit_rate: e.target.value },
                                    }))
                                  }
                                  className="w-24 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-right bg-white dark:bg-gray-700 disabled:opacity-60"
                                />
                                <span className="text-gray-400 ml-1">zł</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* New items to add */}
              {reviewModal.action === "approve" && reviewModal.proposed.filter((p) => !p.order_line_item_id).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nowe pozycje do dodania
                    {isDirector && <span className="text-gray-400 font-normal ml-1">(dyrektor może zmienić wartości)</span>}
                  </p>
                  <div className="rounded border border-blue-200 dark:border-blue-700 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-blue-50 dark:bg-blue-900/20 text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                          <th className="px-2 py-1.5 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość</th>
                          <th className="px-2 py-1.5 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reviewModal.proposed.filter((p) => !p.order_line_item_id).map((p, i) => {
                          const key = `new_${i}`;
                          const ov = newItemOverrides[key] ?? { new_quantity: p.new_quantity, new_unit_rate: p.new_unit_rate };
                          return (
                            <tr key={key} className="border-t border-blue-100 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-900/10">
                              <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">
                                <span className="inline-block text-[10px] font-medium bg-blue-100 dark:bg-blue-900/40 text-[var(--color-primary)] dark:text-blue-300 rounded px-1 mr-1">NOWA</span>
                                {p.line_item_type_name}
                                <span className="text-gray-400 ml-1">[{p.line_item_type_unit}]</span>
                              </td>
                              <td className="px-2 py-1.5 text-right">
                                <input
                                  type="number"
                                  disabled={!isDirector}
                                  value={ov.new_quantity}
                                  onChange={(e) =>
                                    setNewItemOverrides((prev) => ({ ...prev, [key]: { ...ov, new_quantity: e.target.value } }))
                                  }
                                  className="w-20 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-right bg-white dark:bg-gray-700 disabled:opacity-60"
                                />
                              </td>
                              <td className="px-2 py-1.5 text-right">
                                <input
                                  type="number"
                                  disabled={!isDirector}
                                  value={ov.new_unit_rate}
                                  onChange={(e) =>
                                    setNewItemOverrides((prev) => ({ ...prev, [key]: { ...ov, new_unit_rate: e.target.value } }))
                                  }
                                  className="w-24 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-right bg-white dark:bg-gray-700 disabled:opacity-60"
                                />
                                <span className="text-gray-400 ml-1">zł</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {wpwEditsOnReview && (
                <WpwListChangeEditor
                  scope={wpwScope}
                  loading={wpwScopeLoading}
                  delta={reviewWpwDelta}
                  value={reviewWpwPlan}
                  onChange={setReviewWpwPlan}
                />
              )}

              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Komentarz {reviewModal.action === "reject" && <span className="text-red-500">*</span>}
                  {reviewModal.action === "approve" && <span className="text-gray-400 font-normal">(opcja)</span>}
                </label>
                <textarea
                  rows={2}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder={reviewModal.action === "approve" ? "Opcjonalny komentarz do decyzji" : "Powód odrzucenia"}
                />
              </div>
              {reviewErr && (
                <p
                  role="alert"
                  className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2"
                >
                  {reviewErr}
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
              <Button
                size="sm"
                loading={approveMutation.isPending || rejectMutation.isPending}
                onClick={handleReview}
              >
                {reviewModal.action === "approve" ? "Zatwierdź" : "Odrzuć"}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => { setReviewModal(null); setReviewErr(null); setOverrides({}); setNewItemOverrides({}); }}>
                Anuluj
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
